import React, { useState, FormEvent, useEffect } from 'react';
import { translate } from './i18n';
import type { Language } from './i18n';
import './PrepSurvey.scss';
import axios from '../../Data/Axios';
import { logBehavior } from '../../Data/Behavior';
import { useSession } from '@/Recoil/Session/Session';
import { navigateTo } from '@/Utils/routing';

interface PrepSurveyProps {
    onSubmit?: (formData: FormData) => void;
}

// Form data interface
type FormData = {
    name?: string;
    email?: string;
    phone?: string;
    age?: string;
    country?: string;
    timezone?: string;
    nativeLanguage?: string;
    otherLanguages?: string;
    koreanLevel?: string;
    studied_before?: string;
    previous_study?: string;
    learning_goals?: string[];
    focus_skills?: string[];
    timeframe?: string;
    motivation?: string;
    learning_style?: string;
    study_time?: string;
    session_frequency?: string;
    learning_approach?: string;
    cultural_interests?: string[];
    favorite_media?: string;
    concerns?: string;
    expectations?: string;
    other_info?: string;
    selectedOption?: string;
}

// Validation error interface
interface ValidationErrors {
    [key: string]: string;
}

// Email response interface
interface EmailResponse {
    code: number;
    message?: string;
    data?: any;
}

const PrepSurvey: React.FC<PrepSurveyProps> = ({ onSubmit }) => {
    const { session } = useSession();
    const [language, setLanguage] = useState<Language>('id');
    const [formData, setFormData] = useState<FormData>({
        selectedOption: 'four-week'
    });
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    
    // Pre-fill the form with session data when available
    useEffect(() => {
        if (session && session.email) {
            setFormData(prevData => ({
                ...prevData,
                email: session.email
            }));
        } else {
            setTimeout(() => {
                navigateTo('/login?redirectTo=/korean-session/prep-survey');
            }, 200);
        }
    }, [session]);

    // Helper function to translate text
    const t = (key: string): string => translate(key, language);

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        // Clear validation error when field is edited
        clearValidationError(name);
    };

    // Handle checkbox changes (which can have multiple values)
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, checked } = e.target;
        
        // Initialize as empty array if undefined
        const currentValues = (formData[name as keyof FormData] as string[]) || [];
        
        // Add or remove value based on checked state
        let newValues: string[];
        if (checked) {
            newValues = [...currentValues, value];
        } else {
            newValues = currentValues.filter(val => val !== value);
        }
        
        setFormData({
            ...formData,
            [name]: newValues
        });
        
        // Clear validation error when field is edited
        clearValidationError(name);
    };

    // Format form data as HTML table for email
    const formatEmailTableContent = (data: FormData): string => {
        // Create HTML table-based email content
        let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Korean Learning Profile Survey Submission</title>
            <style>
                body {
                    font-family: Arial, sans-serif;
                    line-height: 1.6;
                    color: #333;
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }
                h1 {
                    color: #4c174d;
                    text-align: center;
                    border-bottom: 2px solid #eb90dc;
                    padding-bottom: 10px;
                }
                h2 {
                    color: #eb90dc;
                    border-bottom: 1px solid #eb90dc;
                    padding-bottom: 5px;
                    margin-top: 30px;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 20px;
                }
                th {
                    background-color: #fdf9fe;
                    color: #4c174d;
                    font-weight: bold;
                    text-align: left;
                    padding: 8px;
                    border: 1px solid #eb90dc;
                }
                td {
                    padding: 8px;
                    border: 1px solid #eb90dc;
                }
                .not-provided {
                    color: #999;
                    font-style: italic;
                }
                .section-header {
                    background-color: #fdf9fe;
                    font-weight: bold;
                    text-align: center;
                    color: #4c174d;
                }
            </style>
        </head>
        <body>
            <h1>Korean Learning Profile Survey Submission</h1>
        `;

        // Personal Information section
        htmlContent += `
            <h2>Personal Information</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Name</td>
                    <td>${data.name || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Email</td>
                    <td>${data.email || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Phone</td>
                    <td>${data.phone || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Age</td>
                    <td>${data.age || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Country of Residence</td>
                    <td>${data.country || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Timezone</td>
                    <td>${data.timezone || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;

        // Language Background section
        htmlContent += `
            <h2>Language Background</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Native Language</td>
                    <td>${data.nativeLanguage || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Other Languages</td>
                    <td>${data.otherLanguages || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Current Korean Language Level</td>
                    <td>${data.koreanLevel || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Has studied Korean before</td>
                    <td>${data.studied_before === 'yes' ? 'Yes' : (data.studied_before === 'no' ? 'No' : '<span class="not-provided">Not provided</span>')}</td>
                </tr>
                <tr>
                    <td>Previous Korean learning experience</td>
                    <td>${data.previous_study || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;

        // Learning Goals section
        const goals = data.learning_goals ? (Array.isArray(data.learning_goals) ? data.learning_goals.join(', ') : data.learning_goals) : '<span class="not-provided">None selected</span>';
        const skills = data.focus_skills ? (Array.isArray(data.focus_skills) ? data.focus_skills.join(', ') : data.focus_skills) : '<span class="not-provided">None selected</span>';
        
        htmlContent += `
            <h2>Learning Goals & Motivation</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Why do you want to learn Korean</td>
                    <td>${goals}</td>
                </tr>
                <tr>
                    <td>Skills to focus on</td>
                    <td>${skills}</td>
                </tr>
                <tr>
                    <td>Timeframe for achieving goals</td>
                    <td>${data.timeframe || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>What motivates you to continue learning</td>
                    <td>${data.motivation || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;

        // Learning Preferences section
        htmlContent += `
            <h2>Learning Preferences</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Learning Style</td>
                    <td>${data.learning_style || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Weekly study time</td>
                    <td>${data.study_time || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Preferred session frequency</td>
                    <td>${data.session_frequency || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Preferred learning approach</td>
                    <td>${data.learning_approach || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;

        // Cultural Interests section
        const cultural = data.cultural_interests ? (Array.isArray(data.cultural_interests) ? data.cultural_interests.join(', ') : data.cultural_interests) : '<span class="not-provided">None selected</span>';
        
        htmlContent += `
            <h2>Cultural Interests</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Korean culture interests</td>
                    <td>${cultural}</td>
                </tr>
                <tr>
                    <td>Favorite Korean media</td>
                    <td>${data.favorite_media || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;

        // Additional Information section
        htmlContent += `
            <h2>Additional Information</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Concerns about language learning</td>
                    <td>${data.concerns || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Expectations for sessions</td>
                    <td>${data.expectations || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
                <tr>
                    <td>Other information for tutor</td>
                    <td>${data.other_info || '<span class="not-provided">Not provided</span>'}</td>
                </tr>
            </table>
        `;
        
        // Session Option Selection
        let selectedOptionText = '<span class="not-provided">Not selected</span>';
        if (data.selectedOption === 'one-time') {
            selectedOptionText = 'One-time 1-on-1 Session (Ask for price)';
        } else if (data.selectedOption === 'prep-session') {
            selectedOptionText = 'Prep Session for Multiple 1-on-1 ($5 / Rp 70.000)';
        } else if (data.selectedOption === 'four-week') {
            selectedOptionText = '4 Week In-a-row, 1-on-6 Session ($10 x 4 / Rp 600.000)';
        }
        
        htmlContent += `
            <h2>Selected Session Option</h2>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>Selected Option</td>
                    <td>${selectedOptionText}</td>
                </tr>
            </table>
        `;

        // Close HTML document
        htmlContent += `
        </body>
        </html>
        `;

        return htmlContent;
    };
    
    // Send email function
    const sendEmail = async (data: FormData) => {
        try {
            const htmlContent = formatEmailTableContent(data);
            const studentName = data.name || 'Student';
            
            const response = await axios.post<EmailResponse>('/admin/trigger-email', {
                email: 'dl_mate@webtoon.today', // Recipient email
                subject: `[Korean 1-on-1] Prep Survey: ${studentName}`,
                body: htmlContent,
                groups: 'dl_mate@webtoon.today' // Sender email - must be domain allowed in the API
            });
            
            if (response.data.code === 200) {
                return { success: true };
            } else {
                return { 
                    success: false, 
                    error: response.data.message || 'Failed to send email. Please try again.'
                };
            }
        } catch (error) {
            console.error('Error sending email:', error);
            return { 
                success: false, 
                error: 'Network error. Please try again.'
            };
        }
    };
    
    // Validate form data
    const validateForm = (data: FormData): boolean => {
        const errors: ValidationErrors = {};
        
        // Required fields validation
        const requiredFields = ['name', 'email', 'country', 'timezone', 'nativeLanguage', 'koreanLevel', 'selectedOption'];
        const requiredFieldLabels = {
            'name': t('Full Name'),
            'email': t('Email Address'),
            'country': t('Country of Residence'),
            'timezone': t('Your Timezone'),
            'nativeLanguage': t('Native Language'),
            'koreanLevel': t('Current Korean Language Level'),
            'selectedOption': t('Korean Learning Option')
        };
        
        requiredFields.forEach(field => {
            const value = data[field as keyof FormData];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                errors[field] = t(`${requiredFieldLabels[field as keyof typeof requiredFieldLabels]} is required`);
            }
        });
        
        // Email format validation
        if (data.email && typeof data.email === 'string') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(data.email)) {
                errors.email = t('Please enter a valid email address');
            }
        }
        
        // Learning goals validation (required)
        if (!data.learning_goals || 
            (Array.isArray(data.learning_goals) && data.learning_goals.length === 0)) {
            errors.learning_goals = t('Please select at least one learning goal');
        }
        
        // Set the validation errors
        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            const firstField = Object.keys(errors)[0];
            const errorElement = (
                document.querySelector(`[name="${firstField}"]`)
                || document.getElementById(firstField)
            ) as HTMLElement;
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
            }
        }
        
        // Return true if no errors
        return Object.keys(errors).length === 0;
    };
    
    // Clear validation error for a field when it's being edited
    const clearValidationError = (fieldName: string) => {
        if (validationErrors[fieldName]) {
            const updatedErrors = { ...validationErrors };
            delete updatedErrors[fieldName];
            setValidationErrors(updatedErrors);
        }
    };
    
    // Handle blur event for form fields
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        // If the field has a value after blur, log the edit
        if (value && value.trim() !== '') {
            logBehavior('edit-field', { field: name });
        }
    };
    
    // Handle form submission
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        
        // Validate the form
        const isValid = validateForm(formData);
        if (!isValid) {
            // Scroll to the first error
            const firstErrorField = Object.keys(validationErrors)[0];
            const errorElement = document.getElementById(firstErrorField);
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
            }
            return;
        }
        
        setSubmitting(true);
        setSubmitError(null);
        
        // Call parent onSubmit if provided
        if (onSubmit) {
            onSubmit(formData);
        }
        
        // Send email
        try {
            const result = await sendEmail(formData);
            
            if (result.success) {
                setSubmitSuccess(true);
                // Redirect to the success page instead of showing an alert
                window.location.href = '/korean-session/survey-submitted';
            } else {
                setSubmitError(result.error || t("Failed to send form data. Please try again."));
                alert(t("There was a problem submitting your form. Please try again."));
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitError(t("An unexpected error occurred. Please try again."));
            alert(t("There was a problem submitting your form. Please try again."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="prep-survey-container">
            <div className="page">
                <div className="language-toggle">
                    <select onChange={(e) => setLanguage(e.target.value as Language)} value={language}>
                        <option value="en" selected={language === 'en'}>{t("English")}</option>
                        <option value="id" selected={language === 'id'}>{t("Bahasa Indonesia")}</option>
                    </select>
                </div>

                <div className="header">
                    <div className="logo-container">
                        <img src="/logo-white.svg" alt="Logo MATE" className="logo" />
                    </div>
                    <h1>
                        {language === 'en' ? (
                            <>Korean Learning <span className="gradient-text">Profile</span> Survey</>
                        ) : (
                            <>Survei Profil Belajar Bahasa <span className="gradient-text">Korea</span></>
                        )}
                    </h1>
                </div>
                <div className="slogan">{t("Talk and Learn Korean")}</div>

                <div className="note-box">
                    <p>{t("Your responses will help us tailor our Korean language sessions to your specific needs and learning goals. All information provided will be kept confidential and used only for educational purposes.")}</p>
                </div>

                <form id="studentSurvey" onSubmit={handleSubmit}>
                    <div className="section">
                        <h2>{t("Personal Information")}</h2>
                        
                        <div className="form-group">
                            <label htmlFor="name" className="required">{t("Full Name")}</label>
                            <input 
                                type="text" 
                                id="name" 
                                name="name" 
                                required 
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={validationErrors.name ? 'error' : ''}
                            />
                            {validationErrors.name && (
                                <div className="validation-error">{validationErrors.name}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="email" className="required">{t("Email Address")}</label>
                            <input 
                                type="email" 
                                id="email" 
                                name="email" 
                                value={formData.email || ''}
                                required
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={validationErrors.email ? 'error' : ''}
                            />
                            {validationErrors.email && (
                                <div className="validation-error">{validationErrors.email}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">{t("Phone Number")}</label>
                            <input 
                                type="tel" 
                                id="phone" 
                                name="phone"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="age">{t("Age")}</label>
                            <select 
                                id="age" 
                                name="age"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            >
                                <option value="">{t("Select your age range")}</option>
                                <option value="under18">{t("Under 18")}</option>
                                <option value="18-24">{t("18-24")}</option>
                                <option value="25-34">{t("25-34")}</option>
                                <option value="35-44">{t("35-44")}</option>
                                <option value="45-54">{t("45-54")}</option>
                                <option value="55+">{t("55+")}</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="country" className="required">{t("Country of Residence")}</label>
                            <input 
                                type="text" 
                                id="country" 
                                name="country" 
                                required
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={validationErrors.country ? 'error' : ''}
                            />
                            {validationErrors.country && (
                                <div className="validation-error">{validationErrors.country}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="timezone" className="required">{t("Your Timezone")}</label>
                            
                            <div className="timezone-buttons">
                                <button 
                                    type="button"
                                    className={`timezone-button ${formData.timezone === 'GMT+07:00 (Jakarta)' ? 'active' : ''}`}
                                    onClick={() => {
                                        setFormData({
                                            ...formData,
                                            timezone: 'GMT+07:00 (Jakarta)'
                                        });
                                        clearValidationError('timezone');
                                    }}
                                >
                                    <span className="city-name">Jakarta</span>
                                    <span className="timezone-offset">(+07:00)</span>
                                </button>
                                <button 
                                    type="button"
                                    className={`timezone-button ${formData.timezone === 'GMT+08:00 (Bali)' ? 'active' : ''}`}
                                    onClick={() => {
                                        setFormData({
                                            ...formData,
                                            timezone: 'GMT+08:00 (Bali)'
                                        });
                                        clearValidationError('timezone');
                                    }}
                                >
                                    <span className="city-name">Bali</span>
                                    <span className="timezone-offset">(+08:00)</span>
                                </button>
                                <button 
                                    type="button"
                                    className={`timezone-button ${formData.timezone === 'GMT+09:00 (Jayapura)' ? 'active' : ''}`}
                                    onClick={() => {
                                        setFormData({
                                            ...formData,
                                            timezone: 'GMT+09:00 (Jayapura)'
                                        });
                                        clearValidationError('timezone');
                                    }}
                                >
                                    <span className="city-name">Jayapura</span>
                                    <span className="timezone-offset">(+09:00)</span>
                                </button>
                                <input 
                                    type="text" 
                                    id="timezone" 
                                    name="timezone" 
                                    value={formData.timezone || ''}
                                    required 
                                    placeholder={t("Or enter your timezone (e.g. GMT+8, EST, etc.)")}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={validationErrors.timezone ? 'error' : ''}
                                />
                                {validationErrors.timezone && (
                                    <div className="validation-error">{validationErrors.timezone}</div>
                                )}
                            </div>
                            
                        </div>
                    </div>

                    <div className="section">
                        <h2>{t("Language Background")}</h2>
                        
                        <div className="form-group">
                            <label htmlFor="nativeLanguage" className="required">{t("Native Language")}</label>
                            <input 
                                type="text" 
                                id="nativeLanguage" 
                                name="nativeLanguage" 
                                required
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={validationErrors.nativeLanguage ? 'error' : ''}
                            />
                            {validationErrors.nativeLanguage && (
                                <div className="validation-error">{validationErrors.nativeLanguage}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>{t("Other Languages You Speak")}</label>
                            <textarea 
                                id="otherLanguages" 
                                name="otherLanguages" 
                                placeholder={t("List any other languages you speak and your proficiency level")}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="koreanLevel" className="required">{t("Current Korean Language Level")}</label>
                            <select 
                                id="koreanLevel" 
                                name="koreanLevel" 
                                required
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={validationErrors.koreanLevel ? 'error' : ''}
                            >
                                <option value="">{t("Select your level")}</option>
                                <option value="absolute_beginner">{t("Absolute Beginner (No knowledge)")}</option>
                                <option value="beginner">{t("Beginner (Know Hangul, basic greetings)")}</option>
                                <option value="elementary">{t("Elementary (Simple conversations, basic grammar)")}</option>
                                <option value="intermediate">{t("Intermediate (Can discuss familiar topics)")}</option>
                                <option value="advanced">{t("Advanced (Fluent in most situations)")}</option>
                                <option value="native">{t("Native/Bilingual")}</option>
                            </select>
                            {validationErrors.koreanLevel && (
                                <div className="validation-error">{validationErrors.koreanLevel}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>{t("Have you studied Korean before?")}</label>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="studied_yes" 
                                    name="studied_before" 
                                    value="yes"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="studied_yes">{t("Yes")}</label>
                            </div>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="studied_no" 
                                    name="studied_before" 
                                    value="no"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="studied_no">{t("No")}</label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="previous_study">{t("If yes, please describe your previous Korean learning experience:")}</label>
                            <textarea 
                                id="previous_study" 
                                name="previous_study" 
                                placeholder={t("Describe any courses, self-study, tutoring, etc.")}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>
                    </div>

                    <div className="section">
                        <h2>{t("Learning Goals & Motivation")}</h2>
                        
                        <div className="form-group">
                            <label className="required">{t("Why do you want to learn Korean? (Select all that apply)")}</label>
                            {validationErrors.learning_goals && (
                                <div className="validation-error">{validationErrors.learning_goals}</div>
                            )}
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="goal_travel" 
                                    name="learning_goals" 
                                    value="travel"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="goal_travel">{t("Travel to Korea")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="goal_kpop" 
                                    name="learning_goals" 
                                    value="kpop"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="goal_kpop">{t("K-pop/K-drama interest")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="goal_business" 
                                    name="learning_goals" 
                                    value="business"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="goal_business">{t("Business/Professional")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="goal_academic" 
                                    name="learning_goals" 
                                    value="academic"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="goal_academic">{t("Academic purposes")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="goal_family" 
                                    name="learning_goals" 
                                    value="family"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="goal_family">{t("Korean family/heritage")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="goal_friends" 
                                    name="learning_goals" 
                                    value="friends"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="goal_friends">{t("Korean friends/relationship")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="goal_other" 
                                    name="learning_goals" 
                                    value="other"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="goal_other">{t("Other")}</label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t("What specific skills would you like to focus on?")}</label>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="skill_speaking" 
                                    name="focus_skills" 
                                    value="speaking"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="skill_speaking">{t("Speaking/Conversation")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="skill_listening" 
                                    name="focus_skills" 
                                    value="listening"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="skill_listening">{t("Listening Comprehension")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="skill_reading" 
                                    name="focus_skills" 
                                    value="reading"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="skill_reading">{t("Reading")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="skill_writing" 
                                    name="focus_skills" 
                                    value="writing"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="skill_writing">{t("Writing")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="skill_grammar" 
                                    name="focus_skills" 
                                    value="grammar"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="skill_grammar">{t("Grammar")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="skill_vocabulary" 
                                    name="focus_skills" 
                                    value="vocabulary"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="skill_vocabulary">{t("Vocabulary Building")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="skill_culture" 
                                    name="focus_skills" 
                                    value="culture"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="skill_culture">{t("Cultural Understanding")}</label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="timeframe">{t("What is your timeframe for achieving your Korean language goals?")}</label>
                            <select 
                                id="timeframe" 
                                name="timeframe"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            >
                                <option value="">{t("Select a timeframe")}</option>
                                <option value="1-3months">{t("1-3 months")}</option>
                                <option value="3-6months">{t("3-6 months")}</option>
                                <option value="6-12months">{t("6-12 months")}</option>
                                <option value="1-2years">{t("1-2 years")}</option>
                                <option value="long_term">{t("Long-term learning (2+ years)")}</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="motivation">{t("What motivates you to continue learning even when it gets difficult?")}</label>
                            <textarea 
                                id="motivation" 
                                name="motivation"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>
                    </div>

                    <div className="section">
                        <h2>{t("Learning Preferences")}</h2>
                        
                        <div className="form-group">
                            <label>{t("How would you describe your learning style?")}</label>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="style_visual" 
                                    name="learning_style" 
                                    value="visual"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="style_visual">{t("Visual (learning through images, videos, diagrams)")}</label>
                            </div>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="style_auditory" 
                                    name="learning_style" 
                                    value="auditory"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="style_auditory">{t("Auditory (learning through listening and speaking)")}</label>
                            </div>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="style_reading" 
                                    name="learning_style" 
                                    value="reading"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="style_reading">{t("Reading/Writing (learning through text and notes)")}</label>
                            </div>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="style_kinesthetic" 
                                    name="learning_style" 
                                    value="kinesthetic"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="style_kinesthetic">{t("Kinesthetic (learning through practice and experience)")}</label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t("How much time can you dedicate to studying Korean per week?")}</label>
                            <select 
                                id="study_time" 
                                name="study_time"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            >
                                <option value="">{t("Select time commitment")}</option>
                                <option value="less1">{t("Less than 1 hour")}</option>
                                <option value="1to3">{t("1-3 hours")}</option>
                                <option value="4to7">{t("4-7 hours")}</option>
                                <option value="8plus">{t("8+ hours")}</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>{t("Preferred session frequency:")}</label>
                            <select 
                                id="session_frequency" 
                                name="session_frequency"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            >
                                <option value="">{t("Select frequency")}</option>
                                <option value="once_week">{t("Once a week")}</option>
                                <option value="twice_week">{t("Twice a week")}</option>
                                <option value="thrice_week">{t("Three times a week")}</option>
                                <option value="daily">{t("Daily")}</option>
                                <option value="flexible">{t("Flexible/As needed")}</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>{t("Preferred learning approach:")}</label>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="approach_structured" 
                                    name="learning_approach" 
                                    value="structured"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="approach_structured">{t("Structured (following a curriculum, textbook-based)")}</label>
                            </div>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="approach_conversational" 
                                    name="learning_approach" 
                                    value="conversational"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="approach_conversational">{t("Conversational (learning through natural dialogue)")}</label>
                            </div>
                            <div className="radio-group">
                                <input 
                                    type="radio" 
                                    id="approach_mixed" 
                                    name="learning_approach" 
                                    value="mixed"
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="approach_mixed">{t("Mixed approach")}</label>
                            </div>
                        </div>
                    </div>

                    <div className="section">
                        <h2>{t("Cultural Interests")}</h2>
                        
                        <div className="form-group">
                            <label>{t("Which aspects of Korean culture interest you? (Select all that apply)")}</label>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_kpop" 
                                    name="cultural_interests" 
                                    value="kpop"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_kpop">{t("K-pop")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_kdrama" 
                                    name="cultural_interests" 
                                    value="kdrama"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_kdrama">{t("K-dramas")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_food" 
                                    name="cultural_interests" 
                                    value="food"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_food">{t("Korean Cuisine")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_history" 
                                    name="cultural_interests" 
                                    value="history"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_history">{t("History")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_literature" 
                                    name="cultural_interests" 
                                    value="literature"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_literature">{t("Literature")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_business" 
                                    name="cultural_interests" 
                                    value="business"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_business">{t("Business Culture")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_traditional" 
                                    name="cultural_interests" 
                                    value="traditional"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_traditional">{t("Traditional Culture")}</label>
                            </div>
                            <div className="checkbox-group">
                                <input 
                                    type="checkbox" 
                                    id="culture_modern" 
                                    name="cultural_interests" 
                                    value="modern"
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="culture_modern">{t("Modern Society")}</label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="favorite_media">{t("Do you have any favorite Korean media (shows, music, films) that you'd like to incorporate into your learning?")}</label>
                            <textarea 
                                id="favorite_media" 
                                name="favorite_media"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>
                    </div>

                    <div className="section">
                        <h2>{t("Additional Information")}</h2>
                        
                        <div className="form-group">
                            <label htmlFor="concerns">{t("Do you have any concerns or challenges with language learning that we should be aware of?")}</label>
                            <textarea 
                                id="concerns" 
                                name="concerns"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="expectations">{t("What are your expectations for these Korean language sessions?")}</label>
                            <textarea 
                                id="expectations" 
                                name="expectations"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="other_info">{t("Any other information you'd like to share with your tutor?")}</label>
                            <textarea 
                                id="other_info" 
                                name="other_info"
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                            />
                        </div>
                    </div>

                    {/* <div className="session-options">
                        <h2 className="required">{t("Choose Your Korean Learning Option")}</h2>
                        {validationErrors.selectedOption && (
                            <div className="validation-error option-validation-error">{validationErrors.selectedOption}</div>
                        )}
                        <div className="options-container">
                            <div 
                                className={`option-button ${formData.selectedOption === 'one-time' ? 'selected' : ''}`}
                                onClick={() => {}}
                            >
                                <div className="option-title">{t("One-time 1-on-1 Session")}</div>
                                <div className="option-price">{t("Ask for price")}</div>
                                <div className="option-description">
                                    {t("Individual Korean language session with a tutor")}
                                </div>
                                <div className="recommended-for">
                                    <strong>{t("Recommended for:")}</strong> {t("Those preparing travel in Korea")}
                                </div>
                                <div className="option-example">
                                    {t("Example: Discuss your 3-night/4-day Korean tour plan and get recommendations for buying cosmetics at Olive Young in a 30-minute session.")}
                                </div>
                            </div>
                            
                            <div 
                                className={`option-button ${formData.selectedOption === 'prep-session' ? 'selected' : ''}`}
                                onClick={() => {}}
                            >
                                <div className="option-title">{t("Prep Session for Multiple 1-on-1")}</div>
                                <div className="option-price">$4 / Rp 70.000</div>
                                <div className="option-description">
                                    {t("Preparation sessions to maximize your learning")}
                                </div>
                                <div className="recommended-for">
                                    <strong>{t("Recommended for:")}</strong> {t("Those preparing TOPIK, study abroad, etc.")}
                                </div>
                                <div className="option-example">
                                    {t("Example: We will examine your current level and plan 4-12 sessions to help you achieve your Korean language goals.")}
                                </div>
                            </div>
                            
                            <div 
                                className={`option-button ${formData.selectedOption === 'four-week' ? 'selected' : ''}`}
                                onClick={() => {}}
                            >
                                <div className="option-title">{t("4 Week In-a-row, 1-on-6 Session")}</div>
                                <div className="option-price">$10 x 4 / Rp 700.000</div>
                                <div className="option-description">
                                    {t("Four consecutive weeks of group sessions")}
                                </div>
                                <div className="recommended-for">
                                    <strong>{t("Recommended for:")}</strong> {t("Consistent learners seeking group interaction")}
                                </div>
                                <div className="option-example">
                                    {t("Example: Join a group of Day6 fans for Korean song lessons and engaging conversations about Korean culture.")}
                                </div>
                            </div>
                        </div>
                    </div> */}

                    <button 
                        type="submit" 
                        disabled={submitting || Object.keys(validationErrors).length > 0}
                    >
                        {submitting ? t("Submitting...") : t("Submit Survey")}
                    </button>
                    
                    {submitError && (
                        <div className="error-message">
                            {submitError}
                        </div>
                    )}
                    
                    {submitSuccess && (
                        <div className="success-message">
                            {t("Your survey has been submitted successfully!")}
                        </div>
                    )}
                </form>

                <div className="footer">
                    <p><span className="gradient-text">MATE</span> - {"Talk and Learn Korean"}</p>
                    <p>{t("Thank you for taking the time to complete this survey. Your responses will help us create a personalized learning experience for you.")}</p>
                </div>
            </div>
        </div>
    );
};

export default PrepSurvey;